import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/joy';
import { Link, useParams } from 'react-router-dom';
import { directSupabase } from '../lib/supabaseClient';
import { customerService, type Customer } from '../services/customerService';
import { useProfile } from '../hooks/useProfile';
import type { Project } from '../types/project';
import JobCard from './project-workspace/JobCard';
import { applyWorkflowTaskChange, getFullWorkflowTasksForMode, getWorkflowFlagPatch } from '../utils/workflowUtils';
import {
  areAllProjectJobCardsComplete,
  resolveJobCardFromRouteId,
} from './project-workspace/jobCard.service';
import type { JobCardRecord } from './project-workspace/jobCard.types';

const FitterJobCardPage: React.FC = () => {
  const { projectId: routeId } = useParams<{ projectId: string }>();
  const { isAdmin } = useProfile();
  const [jobCard, setJobCard] = useState<JobCardRecord | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resolved = await resolveJobCardFromRouteId(routeId);
        if (resolved === 'ambiguous') {
          throw new Error('This workspace has more than one job card. Open the specific card from the fitter dashboard.');
        }
        if (!resolved) {
          throw new Error('Job card not found');
        }
        const { data, error: projectError } = await directSupabase
          .from('projects')
          .select('*')
          .eq('id', resolved.project_id)
          .single();
        if (projectError) throw new Error(projectError.message);
        const nextProject = data as Project;
        let nextCustomer: Customer | null = null;
        if (nextProject.customer_id) {
          nextCustomer = await customerService.getLocalCustomerById(String(nextProject.customer_id));
        }
        if (!cancelled) {
          setJobCard(resolved);
          setProject(nextProject);
          setCustomer(nextCustomer);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load job');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [routeId]);

  const patchWorkflowComplete = async (complete: boolean) => {
    if (!project) return;
    const next = getFullWorkflowTasksForMode(project.workflow_tasks || [], project.supply_only || false, project);
    let updated = applyWorkflowTaskChange(next, 'Installation Complete', complete);
    updated = applyWorkflowTaskChange(updated, 'Request Review', complete);
    const flags = getWorkflowFlagPatch(updated, project.supply_only || false, project);
    const { error: updateError } = await directSupabase
      .from('projects')
      .update({
        workflow_tasks: updated,
        supplier_quote_received: flags.supplier_quote_received,
        deposit_received: flags.deposit_received,
        ordered: flags.ordered,
      })
      .eq('id', project.id);
    if (updateError) throw new Error(updateError.message);
    setProject({ ...project, workflow_tasks: updated });
  };

  const handleAttestedComplete = async () => {
    if (!project) return;
    if (!(await areAllProjectJobCardsComplete(project.id))) return;
    await patchWorkflowComplete(true);
  };

  const handleReleased = async () => {
    await patchWorkflowComplete(false);
  };

  const handleProjectPatch = async (patch: Partial<Project>) => {
    if (!project) return;
    const { error: updateError } = await directSupabase
      .from('projects')
      .update(patch)
      .eq('id', project.id);
    if (updateError) throw new Error(updateError.message);
    setProject({ ...project, ...patch });
  };

  return (
    <Box sx={{
      px: { xs: 0.5, sm: 1 },
      py: { xs: 1, md: 2 },
      maxWidth: 720,
      mx: 'auto',
      width: '100%',
      pb: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', md: 2 },
    }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
        gap={1}
      >
        <Typography level="h3" sx={{ fontSize: { xs: '1.35rem', sm: '1.75rem' }, wordBreak: 'break-word' }}>
          {jobCard?.title || project?.project_name || 'Job card'}
        </Typography>
        <Button
          size="md"
          variant="outlined"
          component={Link}
          to="/job-records"
          sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}
        >
          Back to dashboard
        </Button>
      </Stack>
      {loading && <CircularProgress />}
      {error && <Alert color="danger">{error}</Alert>}
      {!loading && !error && project && jobCard && (
        <JobCard
          jobCard={jobCard}
          project={project}
          customer={customer}
          allowAssign={isAdmin}
          showSpecLinks={isAdmin}
          onAttestedComplete={handleAttestedComplete}
          onReleased={handleReleased}
          onProjectPatch={isAdmin ? handleProjectPatch : undefined}
          onJobCardPatch={setJobCard}
        />
      )}
    </Box>
  );
};

export default FitterJobCardPage;
